/**
 * shared.ts — Tipos e helpers do domínio Custo do Pedido.
 *
 * Camada de análise de margem por pedido de venda. Categorias e fornecedores
 * são próprios (não os do Tiny). Navegação por loja (bz|tr).
 */
import { formatarDinheiro } from '@/lib/numero';

export interface Loja {
  id: number;
  nome: string;
  codigo?: string | null;
}

export interface CategoriaOpcao {
  id: number;
  nome: string;
  grupo?: string | null;
  tipo: string; // 'custo' | 'venda'
}

export interface FornecedorOpcao {
  id: number;
  nome: string;
  tipo?: string | null;
}

export interface CustoLinha {
  id?: number;
  categoria_id?: number | null;
  categoria_nome?: string | null;
  grupo?: string | null;
  fornecedor_id?: number | null;
  fornecedor_nome?: string | null;
  descricao?: string | null;
  num_doc?: string | null;
  valor: number;
  situacao?: string | null;
  data?: string | null;
  origem_tipo?: string | null;
  obs?: string | null;
}

export interface PedidoResumo {
  id: number;
  loja_id: number;
  loja_nome?: string | null;
  nr_pedido: string;
  cliente?: string | null;
  categoria_venda_nome?: string | null;
  vendedor?: string | null;
  valor_venda?: number | null;
  /** Vem `null` para quem não tem `custos-pedido.margem.ver`. */
  custo_total?: number | null;
  margem?: number | null;
  margem_pct?: number | null;
  situacao?: string | null;
  origem?: string | null;
  data?: string | null;
  qtd_custos: number;
  // O espelho do Tiny (migration 119, ficha #44).
  situacao_tiny?: string | null;
  /** acompanhar | nao_acompanhar — vazio = EM DÚVIDA. */
  acompanhamento?: 'acompanhar' | 'nao_acompanhar' | null;
  acompanhamento_em?: string | null;
  sumiu_tiny?: string | null;
  sincronizado_tiny?: string | null;
  mudancas_abertas: number;
  pedido_substituto_id?: number | null;
}

export interface PedidoDetalhe extends PedidoResumo {
  categoria_venda_id?: number | null;
  desconto?: number | null;
  forma_pgto?: string | null;
  obs?: string | null;
  custos: CustoLinha[];
  composicao: { grupo: string; valor: number; pct: number }[];
}

export interface Resumo {
  qtd_pedidos: number;
  receita_total: number;
  custo_total: number;
  margem_total: number;
  margem_media_pct?: number | null;
}

/** Corpo enviado à API (POST/PUT). */
export interface PedidoIn {
  loja_id: number;
  nr_pedido: string;
  cliente?: string | null;
  categoria_venda_id?: number | null;
  vendedor?: string | null;
  valor_venda?: number | null;
  desconto?: number | null;
  forma_pgto?: string | null;
  origem?: string | null;
  situacao?: string | null;
  obs?: string | null;
  data?: string | null;
  custos?: CustoLinha[];
}

export const SITUACOES: { value: string; label: string }[] = [
  { value: 'aberto', label: 'Aberto' },
  { value: 'aguardando', label: 'Aguardando' },
  { value: 'encerrado', label: 'Encerrado' },
];

export const rotuloSituacao = (s?: string | null): string =>
  SITUACOES.find((x) => x.value === s)?.label ?? (s ?? '—');

export function badgeSituacao(s?: string | null): string {
  if (s === 'encerrado') return 'text-bg-success';
  if (s === 'aguardando') return 'text-bg-warning';
  return 'text-bg-secondary';
}

/** Cor da margem: verde (boa) / amarelo (apertada) / vermelha (negativa). */
export function corMargem(pct?: number | null): string {
  if (pct == null) return 'text-secondary';
  if (pct < 0) return 'text-danger';
  if (pct < 20) return 'text-warning';
  return 'text-success';
}

/**
 * VAZIO NÃO É ZERO. Antes isto devolvia "R$ 0,00" para `null`, e com o filtro de
 * `custos-pedido.margem.ver` isso passaria a MENTIR: diria que o pedido não
 * custou nada a quem só não tem acesso ao custo. Zero de verdade continua
 * saindo como R$ 0,00, porque `0` não é `null`.
 */
export const fmt = (v: number | null | undefined): string =>
  v == null ? '—' : formatarDinheiro(Number(v));

/** Loja a partir do slug do menu (bz|tr). */
export const codigoDaSlug = (slug?: string): string =>
  (slug ?? 'bz').toUpperCase();

/** Cor de cada grupo no donut de composição.
 *  É o PAPEL, não o tom — quem decide o tom (e o tom do modo escuro) é
 *  `estilos/_cores.scss`. Antes daqui havia seis hex cravados.
 *
 *  ⚠️ Estes valores são `var(...)`, então só funcionam onde entram como CSS.
 *  Em SVG use `style={{ stroke: ... }}`, NUNCA `stroke={...}`: atributo de
 *  apresentação não resolve variável CSS, e o traço sai preto sem erro nenhum. */
export const CORES_GRUPO: Record<string, string> = {
  Mercadoria: 'var(--casaa-custo-mercadoria)',
  Comissões: 'var(--casaa-custo-comissoes)',
  'Logística & Serviços': 'var(--casaa-custo-logistica)',
  'Fiscal / Outros': 'var(--casaa-custo-fiscal)',
  'Sem grupo': 'var(--casaa-custo-sem-grupo)',
};
export const corGrupo = (g: string): string =>
  CORES_GRUPO[g] ?? 'var(--casaa-custo-sem-grupo)';
